/**
 * Shared helpers for Excel tool executors.
 *
 * Provides common patterns used across all executors:
 * - Sheet and range resolution (by name, sheet-qualified address, or active)
 * - Office.js error formatting
 * - Large-range chunking utilities
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* global Excel */

import { asOfficeError, officeErrorLocation } from "@/lib/office/office-error";
import {
  isSameSheetName,
  qualifyAddress,
  splitSheetQualifiedAddress,
} from "../a1";
import { CELL_EDIT_MODE_MESSAGE } from "../cell-edit-mode";

// ---------------------------------------------------------------------------
// Active-sheet pin
// ---------------------------------------------------------------------------
//
// `getActiveWorksheet()` resolves at *sync time*, not when it's called. So if
// the user switches worksheets while a tool/batch is mid-flight, queued writes
// land on whatever sheet is active when the sync flushes — not the sheet that
// was active when the request was issued. To make execution deterministic, the
// executor captures the active sheet name once up front and pins it here;
// `getSheet` then resolves the pinned sheet by name. Client-tool execution is
// serialized (one `Excel.run()` at a time — see useCopilotTools),
// so a single module-level pin is safe.

let activeSheetPin: string | null = null;
let activePinToken = 0;

/**
 * Pin the sheet name that {@link getSheet} treats as "active" for the duration
 * of one tool/batch execution. Returns a token to pass to
 * {@link clearActiveSheetPin} on release.
 */
export function setActiveSheetPin(sheetName: string | null): number {
  activeSheetPin = sheetName;
  activePinToken += 1;
  return activePinToken;
}

/**
 * Release a pin previously set via {@link setActiveSheetPin}. Only clears if no
 * later execution has claimed the pin — this guards against a timed-out (but
 * still-running) `Excel.run()` clearing a newer run's pin when it eventually
 * reaches its `finally` block.
 */
export function clearActiveSheetPin(token: number): void {
  if (token === activePinToken) {
    activeSheetPin = null;
  }
}

/**
 * Read the workbook's active sheet name so it can be pinned. Best-effort:
 * returns `null` (no pin) if the name can't be read, so callers fall back to
 * the live active sheet rather than failing the whole operation.
 */
export async function captureActiveSheetName(
  ctx: Excel.RequestContext
): Promise<string | null> {
  try {
    const active = ctx.workbook.worksheets.getActiveWorksheet();
    active.load("name");
    await ctx.sync();
    return active.name ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sheet resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a worksheet by name, then by the active-sheet pin, then by the live
 * active worksheet. Used by nearly every executor.
 */
export function getSheet(
  ctx: Excel.RequestContext,
  sheetName?: string
): Excel.Worksheet {
  const target = sheetName ?? activeSheetPin ?? undefined;
  return target
    ? ctx.workbook.worksheets.getItem(target)
    : ctx.workbook.worksheets.getActiveWorksheet();
}

/**
 * Resolve the target sheet and bare address for a possibly sheet-qualified
 * range. `Worksheet.getRange()` only accepts a sheet prefix naming the sheet
 * it is called on, so `"FARS!A1"` must be split and routed to `FARS` rather
 * than passed through to whichever sheet `getSheet` resolved.
 *
 * An explicit `sheetName` and a prefix naming a different sheet is an error:
 * silently picking one writes to a sheet the model didn't mean.
 */
export function resolveSheetAddress(
  rangeAddr: string,
  sheetName?: string
): { address: string; sheetName?: string } {
  const parsed = splitSheetQualifiedAddress(rangeAddr);
  if (
    parsed.sheetName !== undefined &&
    sheetName !== undefined &&
    !isSameSheetName(parsed.sheetName, sheetName)
  ) {
    throw new Error(
      `Range "${rangeAddr}" names sheet "${parsed.sheetName}" but sheetName is ` +
        `"${sheetName}". Pass the bare address (e.g. "${parsed.address}") with ` +
        "the sheetName you want."
    );
  }
  return { address: parsed.address, sheetName: sheetName ?? parsed.sheetName };
}

export interface ResolvedRange {
  /** Bare address (sheet prefix removed). */
  address: string;
  /** {@link qualify} applied to `address`. */
  qualifiedAddress: string;
  range: Excel.Range;
  sheet: Excel.Worksheet;
  /** The sheet the range resolved to, when known without a sync. */
  sheetName?: string;
}

/**
 * {@link getSheet} + `getRange` for a possibly sheet-qualified address, so
 * results can report which sheet the tool actually acted on.
 */
export function resolveRange(
  ctx: Excel.RequestContext,
  rangeAddr: string,
  sheetName?: string
): ResolvedRange {
  const target = resolveSheetAddress(rangeAddr, sheetName);
  const sheet = getSheet(ctx, target.sheetName);
  const resolvedName = target.sheetName ?? activeSheetPin ?? undefined;
  return {
    address: target.address,
    qualifiedAddress: qualify(resolvedName, target.address),
    range: sheet.getRange(target.address),
    sheet,
    sheetName: resolvedName,
  };
}

/** Sheet-qualify `address` when the sheet is known; otherwise return it bare. */
export function qualify(
  sheetName: string | undefined,
  address: string
): string {
  return sheetName ? qualifyAddress(sheetName, address) : address;
}

// ---------------------------------------------------------------------------
// Enum resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a model-supplied member name against an `Excel.*` string enum.
 *
 * Office.js enums are objects of `member → "PascalCaseValue"`, so passing the
 * SDK member name (e.g. `"columnClustered"`) returns the value Office.js
 * expects. The schema constrains the model to the valid member names (shared via
 * `@workspace/ai/capabilities/excel/enums`), so this is the runtime counterpart
 * — and it replaces the per-tool `Record<string, Excel.X>` lookup maps.
 *
 * On an unknown key it throws with the full list of valid values so the model
 * can self-correct rather than silently no-op or hit an opaque Office.js error.
 */
export function resolveExcelEnum<T extends Record<string, string>>(
  enumObj: T,
  key: string,
  label: string
): T[keyof T] {
  const value = (enumObj as Record<string, string>)[key];
  if (value === undefined) {
    throw new Error(
      `Invalid ${label} "${key}". Valid values: ${Object.keys(enumObj).join(", ")}`
    );
  }
  return value as T[keyof T];
}

// ---------------------------------------------------------------------------
// Error formatting
// ---------------------------------------------------------------------------

/**
 * Format an OfficeExtension.Error into a user-friendly message.
 *
 * The hints name the Excel tools' own parameters (`range`, `sheetName`) and
 * the `get_sheet_names` tool; keep them in step if those are renamed.
 */
export function formatOfficeError(err: unknown): string {
  if (
    err !== null &&
    typeof err === "object" &&
    (err as { code?: unknown }).code === "InvalidOperationInCellEditMode"
  ) {
    return CELL_EDIT_MODE_MESSAGE;
  }
  const office = asOfficeError(err);
  if (office) {
    const at = officeErrorLocation(office);
    switch (office.code) {
      case "InvalidArgument":
        if (at.includes("DataValidation")) {
          return `Excel rejected the data validation rule${at}. Check that \`operator\` and \`value1\`/\`value2\` suit the validation type (dates as "YYYY-MM-DD"), that a \`formula\` is valid, and that \`listSource\` names an existing range.`;
        }
        return `Invalid range or argument${at}. Pass \`range\` as an A1 address (e.g. "B2:D10" or "Sheet1!B2:D10") on a sheet that exists (call get_sheet_names for exact names); check that the data's shape matches the range.`;
      case "ItemNotFound":
        return `The specified sheet or item was not found${at}. Call get_sheet_names and use the exact name.`;
      case "InvalidOperation":
        return `This operation is not supported on the current selection${at}.`;
      default:
        return `Excel error (${office.code})${at}: ${office.message}`;
    }
  }
  return err instanceof Error ? err.message : "Unknown error";
}

// ---------------------------------------------------------------------------
// Chunking utilities
// ---------------------------------------------------------------------------

/** Maximum rows to read/write in a single context.sync() to stay under ~5 MB. */
export const CHUNK_SIZE = 5000;

/**
 * Process a potentially large range in chunks.
 *
 * Loads `rowCount` and `columnCount` on the range, then calls `processor`
 * for each chunk, syncing after every chunk to stay within the
 * `context.sync()` payload limit (~5 MB).
 *
 * Reference: https://learn.microsoft.com/en-us/office/dev/add-ins/concepts/resource-limits-and-performance-optimization
 */
export async function processLargeRange(
  ctx: Excel.RequestContext,
  range: Excel.Range,
  processor: (chunk: Excel.Range) => void
): Promise<void> {
  range.load("rowCount,columnCount");
  await ctx.sync();

  if (range.rowCount <= CHUNK_SIZE) {
    processor(range);
    await ctx.sync();
    return;
  }

  for (let start = 0; start < range.rowCount; start += CHUNK_SIZE) {
    const size = Math.min(CHUNK_SIZE, range.rowCount - start);
    const chunk = range
      .getRow(start)
      .getResizedRange(size - 1, range.columnCount - 1);
    processor(chunk);
    await ctx.sync(); // sync per chunk to stay within payload limits
  }
}
